/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import {GenericObject} from '@ion/core/src/types/entities';
import {SensorUnitType} from '@ion/core/src/types/general';
import {themedClassName} from '@ion/dls/src/theme/helpers';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {isCurrentTheme} from '@ion/dls/src/theme/useThemeContext';
import {DLSTheme} from '@ion/dls/src/types/types';
import {getFormattedDate} from '@ion/dls/src/utils/ComponentUtils';
import {getCurrentSensorHistoryStyles} from './sensorHistoryStyles';

type ShockSensorHistoryItemProps = {
  item: GenericObject;
  index: number;
  maxImpact?: number;
};

const useStyles = makeStyles((theme: DLSTheme) => {
  const {historyItem, reading, eventText, historyItemTime} = getCurrentSensorHistoryStyles();
  return {
    historyItem : {
      fontSize      : '12px',
      fontWeight    : 'bold',
      letterSpacing : '1px',
      lineHeight    : '18px',
      textTransform : 'uppercase'
    },
    historyItemEvent : {
      color : dlsColor('caGreen')
    },
    historyItemTime : {
      color : dlsColor('grey3')
    },
    tiltReading : {
      color         : dlsColor('grey1'),
      textTransform : 'uppercase'
    },
    historyItemThreshold : {
      color      : dlsColor('caBlue'),
      marginLeft : '-24px'
    },
    event : {
      color : dlsColor('caRed')
    },
    // current theme renders each history row as the shared history tile -
    // the event line keeps its legacy alert color ...
    currentHistoryItem : historyItem,
    currentTiltReading : reading,
    currentEvent       : {
      ...eventText,
      color : dlsColor('caRed')
    },
    currentHistoryItemTime : historyItemTime
  };
});

const ShockSensorHistoryItem = ({
  item,
  index,
  maxImpact
}: ShockSensorHistoryItemProps) => {
  const classes     = useStyles();
  const isCurrent   = isCurrentTheme();
  const unit        = SensorUnitType.GForce;
  const impact      = translate('tracking:scShockSensor.alert');
  const impactArea  = translate(`tracking:scShockSensor.${item?.event?.type}`);
  const eventText   = `${impact}: ${impactArea} (${item?.y} ${unit})`;

  return (
    <div className={themedClassName(classes.historyItem, classes.currentHistoryItem, isCurrent)}
      key={index}>
      <div className={themedClassName(classes.tiltReading, classes.currentTiltReading, isCurrent)}>
        {translate('tracking:scShockSensor.reading')}
      </div>
      <div className={themedClassName(classes.event, classes.currentEvent, isCurrent)}>
        {eventText}
      </div>
      <div className={themedClassName(classes.historyItemTime, classes.currentHistoryItemTime, isCurrent)}>
        {getFormattedDate(item?.eventTime)}
      </div>
    </div>
  );
};

export default ShockSensorHistoryItem;
